import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { EmptyState } from "../EmptyState";
import { Bell, Briefcase, Trash2, CheckCheck, Mail, MailOpen, Search, Bookmark, BellRing } from "lucide-react";

export const NOTIFICATIONS_CHANGED = "f2j:notifications-changed";
export const emitNotificationsChanged = () => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));

const PAGE = 20;
const ALL = "__all__";
const STATUS_TABS = [
  { id: "", label: "Todas" },
  { id: "unread", label: "Não lidas" },
  { id: "read", label: "Lidas" },
];
const PERIODS = [
  { v: ALL, label: "Desde sempre" },
  { v: "1", label: "Últimas 24h" },
  { v: "7", label: "Últimos 7 dias" },
  { v: "30", label: "Últimos 30 dias" },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

/** Histórico completo de notificações do candidato, com filtros, seleção e remoção. */
export function NotificationsTab() {
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [days, setDays] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // {title, description, run}
  const reqId = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const filterParams = useMemo(() => {
    const p = {};
    if (status) p.status = status;
    if (kind) p.kind = kind;
    if (sourceId) p.source_id = sourceId;
    if (days) p.days = days;
    if (q) p.q = q;
    return p;
  }, [status, kind, sourceId, days, q]);

  const load = useCallback(() => {
    const id = ++reqId.current;
    api.get("/notifications", { params: { ...filterParams, limit: PAGE, offset: 0 } })
      .then((r) => {
        if (id !== reqId.current) return;
        setData(r.data);
        setItems(r.data.notifications || []);
        setSelected(new Set());
      })
      .catch((e) => { if (id === reqId.current) { toast.error(formatErr(e)); setData({ total: 0, unread: 0, all: 0, sources: [] }); setItems([]); } });
  }, [filterParams]);

  useEffect(() => { load(); }, [load]);

  // Sincroniza com o sino (ex.: marcou como lida no painel do topo)
  useEffect(() => {
    const h = () => load();
    window.addEventListener(NOTIFICATIONS_CHANGED, h);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, h);
  }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    const id = reqId.current;
    setLoadingMore(true);
    try {
      const r = await api.get("/notifications", { params: { ...filterParams, limit: PAGE, offset: items.length } });
      if (id !== reqId.current) return;
      setItems((prev) => [...prev, ...(r.data.notifications || [])]);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoadingMore(false); }
  };

  const changed = (msg) => {
    if (msg) toast.success(msg);
    emitNotificationsChanged();
  };

  const toggleRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/${n.read ? "unread" : "read"}`);
      changed();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const removeOne = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      changed("Notificação apagada");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulk = async (action, body, msg) => {
    try {
      const { data: r } = await api.post(`/notifications/${action}`, body);
      const n = r.deleted ?? r.updated ?? 0;
      changed(msg(n));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const selIds = [...selected];
  const allOnPageSelected = items.length > 0 && items.every((n) => selected.has(n.id));
  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allOnPageSelected ? new Set() : new Set(items.map((n) => n.id)));

  const hasFilters = Object.keys(filterParams).length > 0;
  const sources = (data?.sources || []).filter((s) => !kind || s.kind === kind);
  const plural = (n, a, b) => `${n} ${n === 1 ? a : b}`;

  return (
    <div className="max-w-3xl" data-testid="notifications-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5 text-amber-500" /> Notificações
          </p>
          <p className="text-sm text-slate-500 mt-1" data-testid="notifications-summary">
            {data ? `${plural(data.all || 0, "notificação", "notificações")} · ${data.unread || 0} por ler` : "A carregar..."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="notif-read-all" variant="outline" size="sm" className="rounded-full" disabled={!data?.unread}
            onClick={() => bulk("bulk-read", { scope: "all" }, (n) => `${plural(n, "notificação marcada", "notificações marcadas")} como lida${n === 1 ? "" : "s"}`)}>
            <CheckCheck className="w-4 h-4 mr-1.5" /> Marcar todas como lidas
          </Button>
          <Button data-testid="notif-delete-read" variant="outline" size="sm" className="rounded-full"
            disabled={!data || (data.all || 0) - (data.unread || 0) === 0}
            onClick={() => setConfirm({
              title: "Apagar notificações lidas?",
              description: `Vais apagar ${plural((data?.all || 0) - (data?.unread || 0), "notificação lida", "notificações lidas")}. Esta ação não pode ser desfeita.`,
              run: () => bulk("bulk-delete", { scope: "read" }, (n) => `${plural(n, "notificação apagada", "notificações apagadas")}`),
            })}>
            <Trash2 className="w-4 h-4 mr-1.5" /> Apagar lidas
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-black/5 p-4 mb-4 space-y-3" data-testid="notifications-filters">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map((t) => (
            <button key={t.id || "all"} type="button" data-testid={`notif-status-${t.id || "all"}`} onClick={() => setStatus(t.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${status === t.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {t.label}
              {t.id === "unread" && data?.unread ? <span className="ml-1.5 text-amber-400">{data.unread}</span> : null}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="relative sm:col-span-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="notif-search" value={qInput} onChange={(e) => setQInput(e.target.value)}
              placeholder="Procurar vaga..." className="h-9 pl-8 text-sm" />
          </div>
          <Select value={kind || ALL} onValueChange={(v) => { setKind(v === ALL ? "" : v); setSourceId(""); }}>
            <SelectTrigger data-testid="notif-kind" className="h-9 text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os tipos</SelectItem>
              <SelectItem value="saved_search">Pesquisas guardadas</SelectItem>
              <SelectItem value="alert">Alertas de emprego</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceId || ALL} onValueChange={(v) => setSourceId(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="notif-source" className="h-9 text-sm"><SelectValue placeholder="Origem" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Todas as origens</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`notif-source-option-${s.id}`}>{s.name || "Sem nome"} ({s.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days || ALL} onValueChange={(v) => setDays(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="notif-period" className="h-9 text-sm"><SelectValue placeholder="Período" /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="notif-filtered-count">{plural(data?.total || 0, "resultado", "resultados")} com estes filtros</span>
            <button type="button" data-testid="notif-clear-filters" className="font-semibold hover:text-slate-900"
              onClick={() => { setStatus(""); setKind(""); setSourceId(""); setDays(""); setQInput(""); }}>
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Barra de seleção */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 mb-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
            <Checkbox data-testid="notif-select-all" checked={allOnPageSelected} onCheckedChange={toggleAll} />
            {selected.size ? `${selected.size} selecionada${selected.size === 1 ? "" : "s"}` : "Selecionar todas"}
          </label>
          <div className="flex flex-wrap gap-2">
            {selected.size > 0 && (
              <>
                <Button data-testid="notif-bulk-read" variant="ghost" size="sm" className="rounded-full h-8 text-xs"
                  onClick={() => bulk("bulk-read", { ids: selIds }, (n) => `${plural(n, "marcada", "marcadas")} como lida${n === 1 ? "" : "s"}`)}>
                  <MailOpen className="w-3.5 h-3.5 mr-1" /> Marcar como lidas
                </Button>
                <Button data-testid="notif-bulk-delete" variant="ghost" size="sm" className="rounded-full h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setConfirm({
                    title: `Apagar ${plural(selIds.length, "notificação", "notificações")}?`,
                    description: "Esta ação não pode ser desfeita.",
                    run: () => bulk("bulk-delete", { ids: selIds }, (n) => `${plural(n, "notificação apagada", "notificações apagadas")}`),
                  })}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Apagar selecionadas
                </Button>
              </>
            )}
            {hasFilters && !selected.size && (data?.total || 0) > 0 && (
              <Button data-testid="notif-delete-filtered" variant="ghost" size="sm" className="rounded-full h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setConfirm({
                  title: `Apagar ${plural(data.total, "notificação", "notificações")} filtradas?`,
                  description: "Vais apagar todas as notificações que correspondem aos filtros atuais. Esta ação não pode ser desfeita.",
                  run: () => bulk("bulk-delete", { scope: "filtered", ...filterParams, days: filterParams.days ? Number(filterParams.days) : undefined },
                    (n) => `${plural(n, "notificação apagada", "notificações apagadas")}`),
                })}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Apagar resultados do filtro
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Lista */}
      {data == null ? (
        <div className="bg-white rounded-2xl border border-black/5 p-8 text-center text-sm text-slate-400">A carregar notificações...</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Bell}
          title={hasFilters ? "Sem notificações com estes filtros" : "Ainda não tens notificações"}
          description={hasFilters ? "Experimenta mudar ou limpar os filtros." : "Guarda uma pesquisa com alerta (pode ser por raio, ex.: até 25 km da tua localidade) e avisamos-te de cada vaga nova."}
          actionLabel={hasFilters ? undefined : "Procurar vagas"}
          onAction={hasFilters ? undefined : () => navigate("/empregos")} />
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 divide-y divide-black/5 overflow-hidden" data-testid="notifications-list">
          {items.map((n) => (
            <div key={n.id} data-testid={`notif-row-${n.id}`} data-read={n.read ? "true" : "false"}
              className={`flex items-start gap-3 px-4 py-3.5 transition-colors ${n.read ? "" : "bg-amber-50/60"}`}>
              <Checkbox data-testid={`notif-select-${n.id}`} className="mt-2" checked={selected.has(n.id)} onCheckedChange={() => toggleSel(n.id)} />
              <span className="mt-0.5 w-9 h-9 rounded-lg bg-slate-900 text-amber-400 flex items-center justify-center shrink-0">
                <Briefcase className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <Link to={n.link || "#"} data-testid={`notif-link-${n.id}`}
                  onClick={() => { if (!n.read) api.post(`/notifications/${n.id}/read`).then(emitNotificationsChanged).catch(() => {}); }}
                  className={`block text-sm hover:underline ${n.read ? "text-slate-700" : "font-semibold text-slate-900"}`}>
                  {n.title}
                </Link>
                {n.body && <p className="text-xs text-slate-500 truncate">{n.body}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-400">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
                    {n.source?.kind === "alert" ? <BellRing className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                    {n.source?.name || (n.source?.kind === "alert" ? "Alerta" : "Pesquisa guardada")}
                  </span>
                  <span>{fmtDate(n.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" data-testid={`notif-toggle-read-${n.id}`} onClick={() => toggleRead(n)}
                  title={n.read ? "Marcar como não lida" : "Marcar como lida"}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100">
                  {n.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                </button>
                <button type="button" data-testid={`notif-delete-${n.id}`} onClick={() => removeOne(n)} title="Apagar"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.has_more && (
        <div className="flex justify-center pt-4">
          <Button data-testid="notif-load-more" variant="outline" className="rounded-full" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? "A carregar..." : `Ver mais (${(data.total || 0) - items.length} restantes)`}
          </Button>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="notif-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="notif-confirm-ok" className="bg-red-600 hover:bg-red-700"
              onClick={() => { const run = confirm?.run; setConfirm(null); if (run) run(); }}>
              Apagar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default NotificationsTab;
